import { ReactNode, useState, useEffect, useRef, useCallback } from 'react'
import { Chart } from "react-google-charts";
import { pdf } from '@react-pdf/renderer';
import Filter, { SelectionAction, SelectionChangeEvent } from './Filter';
import Modal from './Modal';
import { saveAs } from "file-saver";
import { DocumentPdf } from '../lib/DocumentPdf';
import { FatalitiesReport, SelectedFilter, toQuery, getFatalitiesReport, FilterDefinition, getFilters } from '../api/dashboard';
import { useNavigate, useSearchParams } from 'react-router';
import { useIsVisible } from '../hooks/useIsVisible';
import { useFilterToast } from '../hooks/useFilterToast';
import { buildFilterSummary, formatFatalities } from './filterSummary';
import FilterToastStack from './FilterToast';
import {
    ChartRow, barHeight, barOptions, geoOptions, labelOf, largest, rowsOf, sortedDescending, toChartData, yearsOptions
} from './charts';

interface DashboardProps {
    url: string
}

interface PdfImages {
    geo?: string,
    years?: string,
    disciplines?: string,
    incidentTypes?: string
}

interface StatTileProps {
    label: string,
    value: ReactNode,
    detail?: ReactNode,
    hero?: boolean
}

const StatTile = ({ label, value, detail, hero }: StatTileProps) => (
    <div className={`fd-stat${hero ? ' fd-stat--hero' : ''}`}>
        <div className="fd-stat__label">{label}</div>
        <div className="fd-stat__value">{value}</div>
        {detail && <div className="fd-stat__detail">{detail}</div>}
    </div>
);

interface ChartCardProps {
    title: string,
    subtitle?: string,
    rows: ChartRow[],
    /** Column heading for the label column of the data table. */
    labelHeading: string,
    children: ReactNode
}

// Every chart carries its numbers as a table too, so the data does not depend
// on hovering, on colour, or on the chart library loading at all.
const ChartCard = ({ title, subtitle, rows, labelHeading, children }: ChartCardProps) => (
    <div className="fd-card h-100">
        <h4 className="fd-card__title">{title}</h4>
        {subtitle && <p className="fd-card__subtitle">{subtitle}</p>}
        <div className="fd-card__chart">{children}</div>
        <details className="fd-card__table">
            <summary>View as table</summary>
            <table className="table table-sm mb-0">
                <thead>
                    <tr><th scope="col">{labelHeading}</th><th scope="col" className="text-end">Fatalities</th></tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={index}><td>{labelOf(row[0])}</td><td className="text-end">{row[1].toLocaleString()}</td></tr>
                    ))}
                </tbody>
            </table>
        </details>
    </div>
);

const chartLoader = <div className="fd-chart-loading" aria-hidden="true"><span className="spinner-border spinner-border-sm" /></div>;

export default function Dashboard({ url }: DashboardProps) {
    const [data, setData] = useState<FatalitiesReport | null>(null);
    const [isLoading, setLoading] = useState(true);
    const [isRefreshing, setRefreshing] = useState(false);
    const [isBuildingPdf, setBuildingPdf] = useState(false);
    const [queryStringLoaded, setQueryStringLoaded] = useState(false);
    const [selected, setSelected] = useState<SelectedFilter[]>([]);
    const [filterDefinitions, setFilterDefinitions] = useState<FilterDefinition[] | null>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    // The sticky site header covers the top of the viewport (Web.Site's
    // .site-header; .top on the old Igloo site). The visibility test and the
    // jump target both measure it here, from one source, so they cannot
    // disagree about where the headline has to land to count as readable.
    const [headerHeight, setHeaderHeight] = useState(0);
    useEffect(() => {
        const measure = () => {
            const header = document.querySelector('.site-header, .top');
            setHeaderHeight(header ? header.getBoundingClientRect().height : 0);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Watch the results headline, not the whole graph block. Scrolled down to
    // the filters, the bottom of the chart grid can still be on screen while
    // the headline -- the thing a filter change actually updates -- is not,
    // which is precisely the case where the jump link needs to appear.
    // threshold 1 means the whole headline must be in view, and the inset
    // discounts the strip hidden behind the header.
    const { ref: graphRef, node: graphNode, isVisible: isGraphVisible } =
        useIsVisible<HTMLHeadingElement>({
            threshold: 1,
            rootMargin: `-${headerHeight}px 0px 0px 0px`
        });
    const { toasts, show: showToast, dismiss: dismissToast, pause: pauseToast, resume: resumeToast } = useFilterToast();
    // Set by handSelectionChange so the toast only fires for filter changes the
    // user actually made -- not the initial load, and not the query-string
    // hydration below, which also writes to `selected`.
    const pendingToastRef = useRef<SelectedFilter[] | null>(null);
    // Read inside the fetch callback, where the `filterDefinitions` captured by
    // the effect closure may be a render behind.
    const filterDefinitionsRef = useRef<FilterDefinition[] | null>(null);
    // Filters apply fast enough that responses can land out of order; only the
    // newest request is allowed to write state or announce a count.
    const requestIdRef = useRef(0);
    // Written by the off-screen charts' ready events, read when the PDF is built.
    const pdfImagesRef = useRef<PdfImages>({});

    useEffect(() => {
        filterDefinitionsRef.current = filterDefinitions;
    }, [filterDefinitions]);

    const scrollToGraphs = useCallback(() => {
        if (!graphNode) {
            return;
        }
        // Land the headline just clear of the header band the observer discounts,
        // so arriving here reliably counts as "visible" and retires the link.
        const reduceMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({
            top: graphNode.getBoundingClientRect().top + window.scrollY - headerHeight - 16,
            behavior: reduceMotion ? 'auto' : 'smooth'
        });
    }, [graphNode, headerHeight]);

    const handSelectionChange = (event: SelectionChangeEvent) => {
        let selectedLocal: SelectedFilter[] | null = null;
        switch (event.action) {
            case SelectionAction.Add:
                if (!event.name || selected.some(i => i.name === event.name)) {
                    return;
                }
                selectedLocal = [...selected, { name: event.name, value: "" }];
                break;
            case SelectionAction.Remove:
                selectedLocal = selected.filter(i => i.name !== event.name);
                break;
            case SelectionAction.Change:
                if (event.name.endsWith("Date") && event.value === '') {
                    //If Date is empty, remove from the list
                    selectedLocal = selected.filter(i => i.name !== event.name);
                } else {
                    selectedLocal = [...selected.filter(i => i.name !== event.name), { name: event.name, value: event.value }];
                }
                break;
            case SelectionAction.Clear:
                selectedLocal = [];
                break;
        }
        if (selectedLocal != null) {
            // Adding an empty filter row cannot change the results, so don't
            // announce a count the user hasn't actually moved yet.
            pendingToastRef.current = event.action === SelectionAction.Add ? null : selectedLocal;
            setSelected(selectedLocal);
            navigate(`${url}?` + toQuery(selectedLocal));
        }
    }

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setRefreshing(true);
        getFatalitiesReport(selected)
            .then((data) => {
                // A later filter change already landed; this response is stale.
                if (requestId !== requestIdRef.current) {
                    return;
                }
                setData(data);
                const pending = pendingToastRef.current;
                pendingToastRef.current = null;
                if (pending) {
                    showToast(buildFilterSummary(pending, filterDefinitionsRef.current), data.total);
                }
                return getFilters().then((filterDefinitions) => {
                    if (requestId !== requestIdRef.current) {
                        return;
                    }
                    setFilterDefinitions(filterDefinitions);
                });
            })
            .catch((error) => console.error(error))
            .finally(() => {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                    setRefreshing(false);
                }
            });
    }, [selected, showToast]);

    if (isLoading) {
        return (
            <div className="fd-loading" role="status">
                <span className="spinner-border text-primary" aria-hidden="true" />
                <span>Loading struck-by fatality data&hellip;</span>
            </div>
        );
    }
    if (!data) {
        return <div className="alert alert-warning">The struck-by fatality data could not be loaded. Please try again later.</div>;
    }
    if (!queryStringLoaded) {
        if (selected == null || selected.length < 1) {
            let params = [] as SelectedFilter[];
            searchParams.forEach((value, key) => {
                if (!value || key === 'show') {
                    return;
                }
                params.push({ name: key, value });
            });
            setSelected(params);
        }
        setQueryStringLoaded(true);
    }

    const show = searchParams.get('show') === "true";
    const date = new Date(data.date);
    const summary = buildFilterSummary(selected, filterDefinitions);
    const isFiltered = selected.some(s => s.value);

    const states = sortedDescending(data.states).filter(r => r[1] > 0);
    const years = rowsOf(data.years);
    const disciplines = sortedDescending(data.disciplines);
    const incidentTypes = sortedDescending(data.incidentTypes);
    const topState = largest(data.states);
    const topYear = largest(data.years);
    const topDiscipline = largest(data.disciplines);
    const statesAffected = states.length;
    const share = (row: ChartRow | null) => row && data.total > 0 ? `${Math.round(row[1] / data.total * 100)}% of fatalities` : undefined;

    // Rendered through a portal, so it is safe to mount from either branch.
    const toastElement = (
        <FilterToastStack
            toasts={toasts}
            showJumpLink={!isGraphVisible}
            onJump={scrollToGraphs}
            onDismiss={dismissToast}
            onPause={pauseToast}
            onResume={resumeToast}
        />
    );

    // The PDF's chart images come from off-screen copies of the charts, mounted
    // only while a PDF is being made: two GeoCharts drawing at once can leave
    // one blank, and there is no reason to redraw four hidden charts on every
    // filter change.
    const downloadPdf = () => {
        pdfImagesRef.current = {};
        setBuildingPdf(true);
    };

    const pdfChartReady = (key: keyof PdfImages) => ({
        eventName: "ready" as const,
        callback({ chartWrapper }: { chartWrapper: any }) {
            pdfImagesRef.current[key] = chartWrapper?.getChart().getImageURI() as string;
            const images = pdfImagesRef.current;
            if (images.geo && images.years && images.disciplines && images.incidentTypes) {
                buildPdf(images as Required<PdfImages>);
            }
        }
    });

    const buildPdf = async (images: Required<PdfImages>) => {
        try {
            const doc = <DocumentPdf geoUri={images.geo} yearsUri={images.years} disciplinesUri={images.disciplines} incidentTypesUri={images.incidentTypes}
                selected={selected} filterDefinitions={filterDefinitions} fatalities={data.total} />;
            const asPdf = pdf(); // {} is important, throws without an argument
            asPdf.updateContainer(doc);
            const blob = await asPdf.toBlob();
            saveAs(blob, 'StruckBy-Custom-Report.pdf');
        } finally {
            setBuildingPdf(false);
        }
    };

    const results = data.total === 0
        ? (
            <div className="alert alert-info mb-0">
                No incidents match these filters. Try a wider date range, or remove a filter.
            </div>
        )
        : (
            <>
                <div className="row g-3 mb-4">
                    <div className="col-12 col-lg-3">
                        <StatTile hero label="Struck-by fatalities" value={data.total.toLocaleString()} detail={isFiltered ? 'Matching these filters' : 'All reported incidents'} />
                    </div>
                    <div className="col-6 col-lg-3">
                        <StatTile label="Most in one state" value={topState ? labelOf(topState[0]) : '—'}
                            detail={topState ? `${formatFatalities(topState[1])} · ${statesAffected} ${statesAffected === 1 ? 'state' : 'states'} affected` : undefined} />
                    </div>
                    <div className="col-6 col-lg-3">
                        <StatTile label="Deadliest year" value={topYear ? labelOf(topYear[0]) : '—'} detail={topYear ? formatFatalities(topYear[1]) : undefined} />
                    </div>
                    <div className="col-12 col-lg-3">
                        <StatTile label="Most affected discipline" value={topDiscipline ? labelOf(topDiscipline[0]) : '—'} detail={share(topDiscipline)} />
                    </div>
                </div>

                <div className={`row g-4 fd-charts${isRefreshing ? ' is-refreshing' : ''}`}>
                    <div className="col-lg-7">
                        <ChartCard title="Fatalities by state" subtitle="Darker states have more reported fatalities." rows={states} labelHeading="State">
                            <Chart chartType="GeoChart" width="100%" height="340px" data={data.states} options={geoOptions} loader={chartLoader} />
                        </ChartCard>
                    </div>
                    <div className="col-lg-5">
                        <ChartCard title="Fatalities per year" rows={years} labelHeading="Year">
                            <Chart chartType="LineChart" width="100%" height="340px" data={data.years} options={yearsOptions(years.length)} loader={chartLoader} />
                        </ChartCard>
                    </div>
                    <div className="col-lg-6">
                        <ChartCard title="Fatalities by discipline" rows={disciplines} labelHeading="Discipline">
                            <Chart chartType="BarChart" width="100%" height={`${barHeight(disciplines.length)}px`}
                                data={toChartData(disciplines)} options={barOptions(disciplines.length)} loader={chartLoader} />
                        </ChartCard>
                    </div>
                    <div className="col-lg-6">
                        <ChartCard title="Fatalities during response activities" subtitle="What the responder was doing when struck." rows={incidentTypes} labelHeading="Activity">
                            <Chart chartType="BarChart" width="100%" height={`${barHeight(incidentTypes.length)}px`}
                                data={toChartData(incidentTypes)} options={barOptions(incidentTypes.length)} loader={chartLoader} />
                        </ChartCard>
                    </div>
                </div>

                <div className="fd-footnote">
                    <p className="mb-0">
                        Hover over a state, point or bar for its count, or open <em>View as table</em> under any chart.
                        <br />Report generated {date.toLocaleString()}.
                    </p>
                    <button type="button" onClick={downloadPdf} className="btn btn-primary" disabled={isBuildingPdf}>
                        {isBuildingPdf
                            ? <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Preparing PDF&hellip;</>
                            : <><i className="bi bi-file-earmark-pdf me-2" aria-hidden="true" />Download PDF of this report</>}
                    </button>
                </div>
            </>
        );

    return (
        <div className="fd">
            <header className="fd-intro">
                <span className="eyebrow">ReportStruckBy.com data</span>
                <h2 className="fd-intro__title">Struck-By Incident Reports Data</h2>
                <p className="fd-intro__lead">
                    Explore line-of-duty deaths of responders struck while working roadway incidents. Narrow the data by date
                    and incident characteristics; the charts and the PDF follow your filters.
                </p>
            </header>

            <Filter selected={selected} onSelectionChange={handSelectionChange} filterDefinitions={filterDefinitions} />

            <div className="fd-results" aria-busy={isRefreshing}>
                <div className="fd-results__header">
                    <h3 ref={graphRef} className="fd-results__title">
                        Results
                        {isRefreshing && <span className="spinner-border spinner-border-sm ms-3 text-primary" aria-label="Updating" />}
                    </h3>
                    <p className="fd-results__summary">{summary}</p>
                </div>
                {results}
            </div>

            <div className="fd-custom-report">
                <div className="fd-custom-report__intro">
                    <h3 className="h4">Need something the filters can&rsquo;t answer?</h3>
                    <p className="mb-0">Request a custom report from the Emergency Responder Safety Institute. Requests are answered as time and resources allow.</p>
                </div>
                {!show && (
                    <button type="button" className="btn btn-outline-primary flex-shrink-0" onClick={() => navigate(`${url}?${toQuery(selected)}show=true`)}>
                        Request a custom report
                    </button>
                )}
                {show && <Modal url={url} />}
            </div>

            {/* Below are the charts for the PDF. They have to be separate to keep a uniform size. The "{height: 0, overflow: "hidden"}" is the trick for getting them to render correctly. */}
            {isBuildingPdf && data.total > 0 && (
                <div style={{ height: 0, overflow: "hidden" }} aria-hidden="true">
                    <Chart chartType="GeoChart" width="1200px" height="600px" data={data.states}
                        chartEvents={[pdfChartReady("geo")]}
                        options={{ ...geoOptions, backgroundColor: '#ffffff' }} />
                    <Chart chartType="LineChart" width="1200px" height="600px" data={data.years}
                        chartEvents={[pdfChartReady("years")]}
                        options={{ ...yearsOptions(years.length, 1), backgroundColor: '#ffffff' }} />
                    <Chart chartType="BarChart" width="1200px" height="600px" data={toChartData(disciplines)}
                        chartEvents={[pdfChartReady("disciplines")]}
                        options={{ ...barOptions(disciplines.length), backgroundColor: '#ffffff', chartArea: { left: 320, right: 40, top: 20, bottom: 40 } }} />
                    <Chart chartType="BarChart" width="1200px" height="600px" data={toChartData(incidentTypes)}
                        chartEvents={[pdfChartReady("incidentTypes")]}
                        options={{ ...barOptions(incidentTypes.length), backgroundColor: '#ffffff', chartArea: { left: 320, right: 40, top: 20, bottom: 40 } }} />
                </div>
            )}
            {toastElement}
        </div>
    );
}
